import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getOAuthConnection, createOAuthConnection, deleteOAuthConnection } from 'modules/common/api/oAuthConnection';
import { OAuthConnection } from 'modules/common/api/types';
import { OAUTH_QUERY_KEY } from 'services/oauth/queries/useOAuth';

/** Prefix of every provider's connection query; the provider name follows it. */
export const OAUTH_CONNECTION_QUERY_KEY = 'getOAuthConnection';

// `useOAuth` caches the same `oauth/` answer under its own key for five minutes, and the payroll
// pages read `connected` from it. Without invalidating it a reconnect left their "Gusto connection
// required" banner up until that cache went stale.
const useOAuthConnection = (providerName: string) => {
    const queryClient = useQueryClient();
    const {
        data: oAuthResult,
        refetch,
        isLoading,
    } = useQuery<OAuthConnection>({
        queryKey: [OAUTH_CONNECTION_QUERY_KEY, providerName],
        queryFn: () => getOAuthConnection(providerName),
        refetchOnWindowFocus: false,
        retry: false,
    });

    const createConnection: typeof createOAuthConnection = async (...args) => {
        const result = await createOAuthConnection(...args);
        queryClient.invalidateQueries({ queryKey: OAUTH_QUERY_KEY });
        return result;
    };

    const deleteConnection: typeof deleteOAuthConnection = async (...args) => {
        await deleteOAuthConnection(...args);
        queryClient.invalidateQueries({ queryKey: OAUTH_QUERY_KEY });
    };

    return {
        oAuthResult: oAuthResult as OAuthConnection,
        getOAuthConnection,
        createOAuthConnection: createConnection,
        deleteOAuthConnection: deleteConnection,
        refetchoAuthResult: refetch,
        isLoading,
    };
};

export default useOAuthConnection;
